import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ToastContainer } from 'react-toastify';
import { CircularProgress, Box } from '@mui/material';
import { SpeedInsights } from '@vercel/speed-insights/react';

// Styles
import 'react-toastify/dist/ReactToastify.css';
import './styles/toast-cls-fixes.css'; // CLS fixes for toasts
import './styles/variables.css';
import './styles/global.css';
import './styles/mobile.css';
import './styles/buttons.css';

// Config
import { queryClient } from './config/queryClient';
import { initPerformanceMonitoring, observeLongTasks } from './utils/performance';
import { ErrorBoundary } from './components/ErrorBoundary';

// Core components (loaded immediately)
import MainLayout from './components/layout/MainLayout';
import Home from './components/pages/Home';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import Dashboard from './components/pages/Dashboard';
import FeedbackModal from './components/layout/FeedbackModal';
import FeedbackButton from './components/layout/FeedbackButton';
import PageViewTracker from './components/analytics/PageViewTracker';

// Context Providers
import AuthState from './context/auth/AuthState';
import UnifiedMatchState from './context/matches/UnifiedMatchState';
import MatchGroupState from './context/matchGroups/MatchGroupState';
import DeckState from './context/decks/DeckState';
import TeamState from './context/teams/TeamState';
import FriendState from './context/friends/FriendState';
import NotificationState from './context/notifications/NotificationState';
import ThemeState from './context/theme/ThemeState';
import FeedbackState from './context/feedback/FeedbackState';
import SeasonState from './context/seasons/SeasonState';
import PrivateRoute from './components/routing/PrivateRoute';

// Sentry (~150-200 KB) is loaded lazily after the app is interactive — see the
// idle-time dynamic import in App's effect below. It is no longer statically
// imported, so it stays out of the initial bundle and off the critical path.
let sentryInitialized = false;
function initSentryWhenIdle() {
  if (
    sentryInitialized ||
    process.env.NODE_ENV !== 'production' ||
    !process.env.REACT_APP_SENTRY_DSN
  ) {
    return;
  }
  sentryInitialized = true;

  const start = () =>
    import('@sentry/react').then((Sentry) => {
      Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          }),
        ],
        tracesSampleRate: 0.1, // Capture 10% of transactions
        // Skip baseline session replays (expensive); only capture replays on error.
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        environment: process.env.REACT_APP_ENV || 'production',
      });
    });

  if ('requestIdleCallback' in window) {
    requestIdleCallback(start, { timeout: 5000 });
  } else {
    setTimeout(start, 3000);
  }
}

// Lazy-loaded components with prefetch
const LogMatchTCG = lazy(() => import('./components/matches/LogMatchTCG'));
const LogMatchVGC = lazy(() => import('./components/matches/LogMatchVGC'));
const ImportPTCGLMatch = lazy(() => import('./components/matches/ImportPTCGLMatch'));
const TCGAnalytics = lazy(() => import('./components/pages/TCGAnalytics'));
const VGCAnalytics = lazy(() => import('./components/pages/VGCAnalytics'));
const DeckDetail = lazy(() => import('./components/decks/DeckDetail'));
const Teams = lazy(() => import('./components/teams/Teams'));
const TeamForm = lazy(() => import('./components/teams/TeamForm'));
const ShowdownTeamBuilder = lazy(() => import('./components/teams/ShowdownTeamBuilder'));
const ProfileSettings = lazy(() => import('./components/pages/ProfileSettings'));
const Friends = lazy(() => import('./components/pages/Friends'));
const FriendDashboard = lazy(() => import('./components/pages/FriendDashboard'));
const AdminFeedback = lazy(() => import('./components/pages/AdminFeedback'));
const AdminHub = lazy(() => import('./components/pages/AdminHub'));
const AdminManager = lazy(() => import('./components/pages/AdminManager'));
const AdminRestrictedPokemon = lazy(() => import('./components/pages/AdminRestrictedPokemon'));
const AdminDeckCategories = lazy(() => import('./components/pages/AdminDeckCategories'));
const AdminDeckCategorization = lazy(() => import('./components/pages/AdminDeckCategorization'));
const AdminSeasons = lazy(() => import('./components/pages/AdminSeasons'));
const AdminMatches = lazy(() => import('./components/pages/AdminMatches'));
const AdminTournamentCache = lazy(() => import('./components/pages/AdminTournamentCache'));
const AdminArchetypeComparison = lazy(() => import('./components/pages/AdminArchetypeComparison'));
const VisitorsAnalytics = lazy(() => import('./components/pages/VisitorsAnalytics'));
const DeckArchetypeManager = lazy(() => import('./components/admin/DeckArchetypeManager'));
const PatchNotes = lazy(() => import('./components/pages/PatchNotes'));
const ApiDocs = lazy(() => import('./components/pages/ApiDocs'));
const UserSupport = lazy(() => import('./components/pages/UserSupport'));
const MatchGroups = lazy(() => import('./components/matches/MatchGroups'));
const Decks = lazy(() => import('./components/decks/Decks'));
const NewDeckBuilder = lazy(() => import('./components/decks/NewDeckBuilder'));
const LimitlessImport = lazy(() => import('./components/pages/LimitlessImport'));
const GlobalAnalytics = lazy(() => import('./components/pages/GlobalAnalytics'));
const GlobalComparison = lazy(() => import('./components/pages/GlobalComparison'));
const GlobalMatchupMatrix = lazy(() => import('./components/pages/GlobalMatchupMatrix'));
const GlobalTournamentAnalytics = lazy(() => import('./components/pages/GlobalTournamentAnalytics'));
const MyTournamentAnalytics = lazy(() => import('./components/pages/MyTournamentAnalytics'));
const DeckArchetypesList = lazy(() => import('./components/pages/DeckArchetypesList'));
const DeckArchetypeDetail = lazy(() => import('./components/pages/DeckArchetypeDetail'));
const TournamentAnalysisTool = lazy(() => import('./components/pages/TournamentAnalysisTool'));
const Support = lazy(() => import('./components/pages/Support'));

// Loading component — fixed height + explicit spinner size so the
// loader → content swap on lazy routes does not shift layout (CLS).
const PageLoader = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    sx={{ height: '60vh', minHeight: 320 }}
  >
    <CircularProgress size={48} />
  </Box>
);

// Remove console logs in production
if (process.env.NODE_ENV === 'production') {
  console.log = () => {};
  console.debug = () => {};
  console.info = () => {};
  console.warn = () => {};
}

function App() {
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);

  useEffect(() => {
    // Initialize performance monitoring (web-vitals field metrics)
    initPerformanceMonitoring();
    // Long-task observation is dev-only diagnostics; skip its overhead in prod.
    if (process.env.NODE_ENV === 'development') {
      observeLongTasks();
    }
    // Load + initialize Sentry once the app is idle (production only).
    initSentryWhenIdle();

    // Prefetch critical data
    const token = localStorage.getItem('token');
    if (token) {
      // Prefetch user data and decks
      import('./utils/api').then(({ API_ENDPOINTS, apiHelpers }) => {
        apiHelpers.get('/api' + API_ENDPOINTS.AUTH.ME).catch(() => {});
        apiHelpers.get('/api' + API_ENDPOINTS.DECKS.LIST).catch(() => {});
      });
    }

    // Clean up on unmount
    return () => {
      queryClient.clear();
    };
  }, []);

  return (
    <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <Router>
              <ThemeState>
                <AuthState>
                  <UnifiedMatchState>
                    <MatchGroupState>
                      <DeckState>
                        <TeamState>
                          <FriendState>
                            <NotificationState>
                              <FeedbackState>
                                <SeasonState>
                                  <PageViewTracker />
                                <Routes>
                                    {/* Redirect routes for backward compatibility */}
                                    <Route path="/admin" element={<Navigate to="/admin/hub" replace />} />
                                    <Route path="/analytics" element={<Navigate to="/analytics/tcg/matches" replace />} />
                                    <Route path="/analytics-vgc" element={<Navigate to="/analytics/vgc/matches" replace />} />
                                    <Route path="/tcg-match-analytics" element={<Navigate to="/analytics/tcg/matches" replace />} />
                                    <Route path="/tcg-deck-analytics" element={<Navigate to="/analytics/tcg/decks" replace />} />
                                    <Route path="/vgc-match-analytics" element={<Navigate to="/analytics/vgc/matches" replace />} />
                                    <Route path="/vgc-team-analytics" element={<Navigate to="/analytics/vgc/teams" replace />} />
                                    <Route path="/log-match" element={<Navigate to="/log-match/tcg" replace />} />
                                    <Route path="/log-match-vgc" element={<Navigate to="/log-match/vgc" replace />} />
                                    
                                    {/* All routes wrapped in layout - accessible to both guests and authenticated users */}
                                    <Route element={<MainLayout />}>
                                      <Route path="/" element={<Home />} />
                                      <Route path="/login" element={<Login />} />
                                      <Route path="/register" element={<Register />} />
                                      <Route path="/forgot-password" element={<ForgotPassword />} />
                                      <Route path="/reset-password/:token" element={<ResetPassword />} />
                                      <Route path="/dashboard" element={<Dashboard />} />
                                      
                                      {/* Lazy loaded routes */}
                                      <Route path="/log-match/tcg" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <LogMatchTCG />
                                        </Suspense>
                                      } />
                                      <Route path="/log-match/vgc" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <LogMatchVGC />
                                        </Suspense>
                                      } />
                                      <Route path="/edit-match/tcg/:id" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <LogMatchTCG />
                                        </Suspense>
                                      } />
                                      <Route path="/edit-match/vgc/:id" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <LogMatchVGC />
                                        </Suspense>
                                      } />
                                      <Route path="/import-match" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <ImportPTCGLMatch />
                                        </Suspense>
                                      } />
                                      <Route path="/import-limitless" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <LimitlessImport />
                                        </Suspense>
                                      } />
                                      <Route path="/analytics/tcg/matches" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <TCGAnalytics tab="matches" />
                                        </Suspense>
                                      } />
                                      <Route path="/analytics/tcg/decks" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <TCGAnalytics tab="decks" />
                                        </Suspense>
                                      } />
                                      <Route path="/analytics/vgc/matches" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <VGCAnalytics tab="matches" />
                                        </Suspense>
                                      } />
                                      <Route path="/analytics/vgc/teams" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <VGCAnalytics tab="teams" />
                                        </Suspense>
                                      } />
                                      <Route path="/analytics/global" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <GlobalAnalytics />
                                        </Suspense>
                                      } />
                                      <Route path="/analytics/global/comparison" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <GlobalComparison />
                                        </Suspense>
                                      } />
                                      <Route path="/analytics/global/matrix" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <GlobalMatchupMatrix />
                                        </Suspense>
                                      } />
                                      {/* Tournament routes - available to all logged-in members */}
                                      <Route path="/tournament/analytics" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <GlobalTournamentAnalytics />
                                        </Suspense>
                                      } />
                                      <Route path="/tournament/my-analytics" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <MyTournamentAnalytics />
                                        </Suspense>
                                      } />
                                      <Route path="/tournament/decks" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <DeckArchetypesList />
                                        </Suspense>
                                      } />
                                      <Route path="/tournament/decks/:deckId" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <DeckArchetypeDetail />
                                        </Suspense>
                                      } />
                                      <Route path="/tournament/analyze" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <TournamentAnalysisTool />
                                        </Suspense>
                                      } />
                                      <Route path="/lists" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <Decks />
                                        </Suspense>
                                      } />
                                      <Route path="/lists-vgc" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <Teams />
                                        </Suspense>
                                      } />
                                      <Route path="/decks" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <Decks />
                                        </Suspense>
                                      } />
                                      <Route path="/decks/new" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <NewDeckBuilder />
                                        </Suspense>
                                      } />
                                      <Route path="/decks/edit/:id" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <NewDeckBuilder />
                                        </Suspense>
                                      } />
                                      <Route path="/decks/:id" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <DeckDetail />
                                        </Suspense>
                                      } />
                                      <Route path="/teams" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <Teams />
                                        </Suspense>
                                      } />
                                      <Route path="/teams/new" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <TeamForm />
                                        </Suspense>
                                      } />
                                      <Route path="/teams/edit/:id" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <TeamForm />
                                        </Suspense>
                                      } />
                                      <Route path="/teams/import" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <ShowdownTeamBuilder />
                                        </Suspense>
                                      } />
                                      <Route path="/profile-settings" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <ProfileSettings />
                                        </Suspense>
                                      } />
                                      <Route path="/friends" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <Friends />
                                        </Suspense>
                                      } />
                                      <Route path="/friends/:friendId" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <FriendDashboard />
                                        </Suspense>
                                      } />
                                      <Route path="/match-groups" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <MatchGroups />
                                        </Suspense>
                                      } />
                                      <Route path="/patch-notes" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <PatchNotes />
                                        </Suspense>
                                      } />
                                      <Route path="/api-docs" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <ApiDocs />
                                        </Suspense>
                                      } />
                                      <Route path="/support" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <UserSupport />
                                        </Suspense>
                                      } />
                                      <Route path="/donate" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <Support />
                                        </Suspense>
                                      } />
                                      
                                      {/* Admin routes */}
                                      <Route path="/admin/hub" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <AdminHub />
                                        </Suspense>
                                      } />
                                      <Route path="/admin/feedback" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <AdminFeedback />
                                        </Suspense>
                                      } />
                                      <Route path="/admin/managers" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <AdminManager />
                                        </Suspense>
                                      } />
                                      <Route path="/admin/decks" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <AdminDeckCategories />
                                        </Suspense>
                                      } />
                                      <Route path="/admin/restricted-pokemon" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <AdminRestrictedPokemon />
                                        </Suspense>
                                      } />
                                      <Route path="/admin/deck-categories" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <AdminDeckCategories />
                                        </Suspense>
                                      } />
                                      <Route path="/admin/deck-categorization" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <AdminDeckCategorization />
                                        </Suspense>
                                      } />
                                      <Route path="/admin/seasons" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <AdminSeasons />
                                        </Suspense>
                                      } />
                                      <Route path="/admin/matches" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <AdminMatches />
                                        </Suspense>
                                      } />
                                      <Route path="/admin/tournament-cache" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <AdminTournamentCache />
                                        </Suspense>
                                      } />
                                      <Route path="/admin/archetype-comparison" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <AdminArchetypeComparison />
                                        </Suspense>
                                      } />
                                      <Route path="/admin/visitors" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <VisitorsAnalytics />
                                        </Suspense>
                                      } />
                                      <Route path="/admin/deck-archetype-manager" element={
                                        <Suspense fallback={<PageLoader />}>
                                          <DeckArchetypeManager />
                                        </Suspense>
                                      } />
                                    </Route>
                                  </Routes>
                                  
                                  {/* Global components */}
                                  <FeedbackButton onClick={() => setFeedbackOpen(true)} />
                                  <FeedbackModal
                                    isOpen={feedbackOpen}
                                    onClose={() => setFeedbackOpen(false)}
                                  />
                                  
                                  {/* Toast notifications */}
                                  <ToastContainer
                                    position="bottom-right"
                                    autoClose={4000}
                                    hideProgressBar={false}
                                    newestOnTop
                                    closeOnClick
                                    rtl={false}
                                    pauseOnFocusLoss
                                    draggable
                                    pauseOnHover
                                    theme="colored"
                                  />
                                  
                                  {/* Vercel Speed Insights */}
                                  <SpeedInsights />
                                </SeasonState>
                              </FeedbackState>
                            </NotificationState>
                          </FriendState>
                        </TeamState>
                      </DeckState>
                    </MatchGroupState>
                  </UnifiedMatchState>
                </AuthState>
              </ThemeState>
          </Router>

          {/* React Query Devtools - only in development */}
          {process.env.NODE_ENV === 'development' && (
            <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
          )}
        </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;